import { useState, useCallback } from "react";
import { UserDrawnBox } from "@labeling-tools/ui-components/src/box-draw-image/types";

export const useBoxDrawState = () => {
  const [selectedExistingBoxIndex, setSelectedExistingBoxIndex] = useState<
    number | null
  >(null);
  const [selectedUserDrawnBoxIndex, setSelectedUserDrawnBoxIndex] = useState<
    number | null
  >(null);
  const [hiddenExistingBoxIndices, setHiddenExistingBoxIndices] = useState<
    Set<number>
  >(new Set());
  const [hiddenUserDrawnBoxIndices, setHiddenUserDrawnBoxIndices] = useState<
    Set<number>
  >(new Set());
  const [deletedBoxIndices, setDeletedBoxIndices] = useState<Set<number>>(
    new Set(),
  );
  const [userDrawnBoxes, setUserDrawnBoxes] = useState<UserDrawnBox[]>([]);

  const handleUserDrawnBoxesChange = useCallback((boxes: UserDrawnBox[]) => {
    setUserDrawnBoxes(boxes);
  }, []);

  const handleBoxView = useCallback(
    (index: number, isUserDrawn: boolean = false) => {
      if (isUserDrawn) {
        setHiddenUserDrawnBoxIndices((prev) => {
          const next = new Set(prev);
          if (next.has(index)) {
            next.delete(index);
            setSelectedUserDrawnBoxIndex(index);
          } else {
            next.add(index);
            setSelectedUserDrawnBoxIndex(null);
          }
          return next;
        });
        setSelectedExistingBoxIndex(null);
      } else {
        setHiddenExistingBoxIndices((prev) => {
          const next = new Set(prev);
          if (next.has(index)) {
            next.delete(index);
            setSelectedExistingBoxIndex(index);
          } else {
            next.add(index);
            setSelectedExistingBoxIndex(null);
          }
          return next;
        });
        setSelectedUserDrawnBoxIndex(null);
      }
    },
    [],
  );

  const handleBoxDelete = useCallback(
    (index: number, isUserDrawn: boolean = false) => {
      if (isUserDrawn) {
        // Remove user-drawn box from array
        setUserDrawnBoxes((prev) => prev.filter((_, i) => i !== index));
        setSelectedUserDrawnBoxIndex((prev) => {
          if (prev === index) return null;
          if (prev !== null && prev > index) return prev - 1;
          return prev;
        });
        // Adjust hidden indices after removal
        setHiddenUserDrawnBoxIndices((prev) => {
          const next = new Set<number>();
          for (const i of prev) {
            if (i < index) next.add(i);
            else if (i > index) next.add(i - 1);
          }
          return next;
        });
      } else {
        setDeletedBoxIndices((prev) => new Set(prev).add(index));
        setHiddenExistingBoxIndices((prev) => {
          const next = new Set(prev);
          next.add(index);
          return next;
        });
        setSelectedExistingBoxIndex((prev) => (prev === index ? null : prev));
      }
    },
    [],
  );

  const handleBoxRestore = useCallback(
    (index: number) => {
      setDeletedBoxIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
      setHiddenExistingBoxIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    },
    [],
  );

  const resetBoxDrawState = useCallback(() => {
    setSelectedExistingBoxIndex(null);
    setSelectedUserDrawnBoxIndex(null);
    setHiddenExistingBoxIndices(new Set());
    setHiddenUserDrawnBoxIndices(new Set());
    setDeletedBoxIndices(new Set());
    setUserDrawnBoxes([]);
  }, []);

  return {
    selectedExistingBoxIndex,
    setSelectedExistingBoxIndex,
    selectedUserDrawnBoxIndex,
    setSelectedUserDrawnBoxIndex,
    hiddenExistingBoxIndices,
    hiddenUserDrawnBoxIndices,
    deletedBoxIndices,
    userDrawnBoxes,
    handleBoxView,
    handleBoxDelete,
    handleBoxRestore,
    handleUserDrawnBoxesChange,
    resetBoxDrawState,
  };
};
